import { MemberGroups } from "shared-types";
import PageKey from "config/PageKey";

import { INavigation } from "models/navigation";
import {
  IAgentQuickSearchPageTranslation,
  IBreakdownPageTranslation,
  ICommonTranslation,
  IFollowUpPageTranslation,
  IJobsPageTranslation,
  IRolePageTranslation,
  ISelfTapeRequestNotificationsPageTranslation,
  ISelfTapeRequestPageTranslation,
  ISelfTapeUploadPageTranslation,
} from "models/translation";

export const commonFallbackTexts = {
  headerLabels: {
    signIn: "Sign in",
    logoLabel: "Spotlight Logo",
    flagLabel: "Switch language",
    burger: {
      labelOpen: "Open Navigation Menu",
      labelClose: "Close Navigation Menu",
    },
  },
};

export interface IFallbackPageTranslation {
  common: ICommonTranslation;
  [PageKey.Jobs]: IJobsPageTranslation;
  [PageKey.Breakdown]: IBreakdownPageTranslation;
  [PageKey.Role]: IRolePageTranslation;
  [PageKey.FollowUp]: IFollowUpPageTranslation;
  [PageKey.SelfTapeUpload]: ISelfTapeUploadPageTranslation;
  [PageKey.SelfTapeRequest]: ISelfTapeRequestPageTranslation;
  [PageKey.SelfTapeRequestNotifications]: ISelfTapeRequestNotificationsPageTranslation;
  [PageKey.AgentQuickSearch]: IAgentQuickSearchPageTranslation;
}

export const fallbackTranslation: IFallbackPageTranslation = {
  common: {
    followups: { updateLabel: "Update" },
    breakdowns: {
      closingDateLabels: [
        { TypeId: "today", Translation: "Closes today at {time}" },
        { TypeId: "tomorrow", Translation: "Closes tomorrow at {time}" },
        { TypeId: "other", Translation: "Closes {date} at {time}" },
        { TypeId: "closed", Translation: "Closed" },
      ],
      linkmailTypeAmendment: "Amendment #{number}",
      sentOnFormat: "Sent on {date}",
      linkmailTypeDuplicate: "Duplicate",
      viewExclusiveLabel: "Exclusive",
      viewExclusiveTooltip: "Exclusive",
      dateTimeFormat: "{date} at {time}",
      localHire: "Local hire",
      isRoleCastLabelText: "Role Cast",
    },
    grid: {
      senderNameAnonymous: "Anonymous",
      readUnreadButtonOptions: [
        { TypeId: "markAsRead", Translation: "Mark as read" },
        { TypeId: "markAsUnread", Translation: "Mark as unread" },
      ],
      resultStats: "Showing {resultFrom}-{resultTo} of {resultTotal} result(s)",
      selectAll: "Select all",
      refreshResultsLabel: "Refresh results",
    },
    pages: {
      pageTitleFormat: "{pageName} | Spotlight",
      pageTitles: [
        {
          TypeId: "jobsFeedMatchingMySettings",
          Translation: "Jobs feed - Matching my filters",
        },
        {
          TypeId: "jobsFeedAllOpportunities",
          Translation: "Jobs feed - All opportunities",
        },
        {
          TypeId: "jobsFeedProjects",
          Translation: "Jobs feed - Projects",
        },
        {
          TypeId: "jobsFeedAllPaid",
          Translation: "Jobs feed - All paid",
        },
        {
          TypeId: "jobsFeedSelfTapeRequests",
          Translation: "Self-tape requests",
        },
        {
          TypeId: "followUp",
          Translation: '"{followUpName}"',
        },
        {
          TypeId: "breakdown",
          Translation: '"{breakdownName}"',
        },
        {
          TypeId: "role",
          Translation: '"{roleName}"',
        },
        {
          TypeId: "selfTapeAddVideo",
          Translation: '"{selfTapeName}" - Add video',
        },
        {
          TypeId: "selfTapeRequest",
          Translation: '"{selfTapeName}"',
        },
        {
          TypeId: "agentQuickSearch",
          Translation: "Performer search",
        },
      ],
    },
    validationMessages: { validationMessagesRequired: "Required" },
    reportConcernPopup: {
      reportConcernCancelButton: "Cancel",
      reportConcernDetailsPlaceholder: "Please describe the details here",
      reportConcernTypeDropdownLabel:
        "What concerns do you have about this production:",
      reportConcernSendButtonLoading: "Loading",
      reportConcernTypeDropdownPlaceholder: "Select option",
      reportConcernTitle: "Report concern",
      reportConcernDetailsLabel: "Details",
      reportConcernSendButton: "Send",
    },
    print: {
      printCancelButton: "Close",
      printSubmitButton: "Print",
    },
  },
  [PageKey.Jobs]: {
    shared: {
      filterSortingOptions: [
        {
          TypeId: "senderNameAscending",
          Translation: "Sort: Sender (Surname A-Z)",
        },
        {
          TypeId: "senderNameDescending",
          Translation: "Sort: Sender (Surname Z-A)",
        },
        {
          TypeId: "dateSentNewest",
          Translation: "Sort: Date sent (Most recent)",
        },
        {
          TypeId: "dateSentOldest",
          Translation: "Sort: Date sent (Least recent)",
        },
        {
          TypeId: "closesAscending",
          Translation: "Sort: Closes (Ascending)",
        },
        {
          TypeId: "closesDescending",
          Translation: "Sort: Closes (Descending)",
        },
      ],
      filterFilterDrawer: [
        {
          TypeId: "senderCategory",
          Translation: "Sender",
        },
      ],
      suggestedLabel: "Suggested",
      nudgedLabel: "Nudged",
      filterContractTypeFilter: "Contract type",
      pageHeaderButtonLabel: "Change filter settings",
      breadcrumb: [
        {
          TypeId: "MyAccount",
          Translation: "My Account",
        },
      ],
      filterProductionTypeFilter: "Production type",
      filterDateFilter: "Date sent",
      roleTitleFormat: "{role} in {breakdown}",
      playingAge: "Playing age",
      playingAgeFromLabel: "From",
      playingAgeFromPlaceholder: "Yr",
      playingAgeToLabel: "To",
      playingAgeToPlaceholder: "Yr",
      playingAgeEmptyLabel: "Please Select",
      yearLabel: "yr",
      yearsLabel: "yrs",
      playingAgeOver: "Over",
      playingAgeUnder: "Under",
      locationLabel: "Location",
      searchLocationLabel:
        "For privacy and security, we only allow you to search by region or country.",
      searchLocationPlaceholder: "Search",
    },
    agent: {
      agentPageHeaderLabels: [
        {
          TypeId: "matchingMyFilter",
          Translation: "Matching my filter",
        },
        {
          TypeId: "allOpportunities",
          Translation: "All opportunities",
        },
        {
          TypeId: "clientAlerts",
          Translation: "Client alerts",
        },
        {
          TypeId: "selfTapeRequests",
          Translation: "Self-tape requests",
        },
      ],
      agentFilterFilterOptions: [
        {
          TypeId: "allBreakdowns",
          Translation: "Show all breakdowns",
        },
        {
          TypeId: "unreadBreakdowns",
          Translation: "Show unread breakdowns",
        },
        {
          TypeId: "readBreakdowns",
          Translation: "Show read breakdowns",
        },
        {
          TypeId: "breakdownsWithSuggestions",
          Translation: "Show breakdowns with suggestions",
        },
      ],
      agentTabs: [
        {
          TypeId: "matchingMyFilter",
          Translation: "Matching my filter {count}",
        },
        {
          TypeId: "allOpportunities",
          Translation: "All opportunities {count}",
        },
        {
          TypeId: "clientAlerts",
          Translation: "Client alerts {count}",
        },
        {
          TypeId: "selfTapeRequests",
          Translation: "Self-tape requests {count}",
        },
      ],
      agentFilterSearch: "Search breakdown title or sender",
      agentClientAlertsNudgedTitle: "Nudge for {Role name} in {Breakdown name}",
      agentClientAlertsSearchPlaceholder: "Search sender name",
      agentClientAlertsSubmittedTitle:
        "Submitted themselves for {Role name} in {Breakdown name}",
    },
    performer: {
      performerFilterSearch: "Search role title or sender",
      performerPageHeaderLabels: [
        {
          TypeId: "matchingMyFilter",
          Translation: "Matching my filter",
        },
        {
          TypeId: "allPaid",
          Translation: "All paid",
        },
        {
          TypeId: "allOpportunities",
          Translation: "All opportunities",
        },
        {
          TypeId: "selfTapeRequests",
          Translation: "Self-tape requests",
        },
      ],
      performerTabs: [
        {
          TypeId: "matchingMyFilter",
          Translation: "Matching my filter {count}",
        },
        {
          TypeId: "allPaid",
          Translation: "All paid {count}",
        },
        {
          TypeId: "allOpportunities",
          Translation: "All opportunities {count}",
        },
        {
          TypeId: "selfTapeRequests",
          Translation: "Self-tape requests {count}",
        },
        {
          TypeId: "mySuggestions",
          Translation: "My Suggestions",
        },
      ],
      performerFilterFilterOptions: [
        {
          TypeId: "allRoles",
          Translation: "Show all roles",
        },
        {
          TypeId: "unreadRoles",
          Translation: "Show unread roles",
        },
        {
          TypeId: "readRoles",
          Translation: "Show read roles",
        },
        {
          TypeId: "rolesWithSuggestions",
          Translation: "Show roles with suggestions",
        },
      ],
      mySuggestionsHeader: "My suggestions",
      productionTitleLabel: "Production title",
      typeLabel: "Type",
      roleLabel: "Role",
      sentByLabel: "Sent by",
      suggestedDateLabel: "Suggested",
      roleStatusLabel: "Role Status",
      roleStatusOpen: "Open",
      roleStatusClosed: "Closed",
      roleStatusCast: "Role Cast",
      searchPlaceholder: "Search production title",
      datePlaceholder: "Select date range",
      confidentialProduction: "Confidential production",
      confidentialProductionTooltipContent:
        "This breakdown was sent directly to your agent",
      allStatuses: "All",
      downloadErrorText:
        "Download has been failed, please reduce the number of selected suggestions and try again.",
      download: "Download",
      statusFilterPlaceholder: "Status",
    },
  },
  [PageKey.Breakdown]: {
    header: {
      breadcrumb: [
        { TypeId: "MyAccount", Translation: "My account" },
        { TypeId: "JobsFeed", Translation: "Jobs feed" },
      ],
      pageHeaderElements: [
        {
          index: 0,
          label: "Back to jobs feed",
          id: "BackToJobsFeed",
          icon: "https://spotlight-cmsmedia-devaws.s3-eu-west-1.amazonaws.com/media/1193/misc_twoclients.svg",
        },
        {
          index: 1,
          label: "Print",
          id: "Print",
          icon: "https://spotlight-cmsmedia-devaws.s3-eu-west-1.amazonaws.com/media/1182/print.svg",
        },
      ],
    },
    view: {
      viewProjectOverviewLabel: "Project overview",
      viewSentOnFormat: "Sent on {date}",
      viewProductionCompanyLabel: "Production company",
      viewRoleAnyHeight: "Any height",
      viewMessage:
        "<p>Please note that Spotlight is not involved in compiling the job posts which appear on the Spotlight Link. Spotlight is committed to equal opportunities for all performers, irrespective of sex, age, race, disability, religion, sexual orientation, marital status or any other criteria not related to skills or abilities. Where sex, age, height and or/appearance has been specified on a post, the casting director has confirmed that such characteristics are a genuine occupational requirement of the role</p>",
      viewRolePlayingAgeFormat: "{min}–{max} year(s)",
      viewProductionDirectorLabel: "Director:",
      viewRolesHideLabel: "Hide roles with no suggestions",
      viewRoleHeightLabel: "Height:",
      viewProductionContractTypeLabel: "Contract type:",
      viewRoleAnyAppearance: "Any appearance",
      viewRoleAnyGender: "Any gender",
      viewCastingLocationLabel: "Casting location:",
      viewCastingDirectorLabel: "Casting director:",
      viewRoleContractTypeLabel: "Contract type:",
      viewRoleAppearanceLabel: "Appearance:",
      viewClosingDateLabels: [
        { TypeId: "today", Translation: "Closing today at {time}" },
        { TypeId: "tomorrow", Translation: "Closing tomorrow at {time}" },
        { TypeId: "other", Translation: "Closing: {date} at {time}" },
        { TypeId: "closed", Translation: "Closed" },
      ],
      viewSentByAnonymous:
        "The sender has chosen not to show their name or contact details.",
      viewRoleContractTypeInfoLabel: "Contract info:",
      viewProductionLocationLabel: "Production location:",
      viewRolesCollapserOptions: [
        { TypeId: "expand", Translation: "Expand all" },
        { TypeId: "collapse", Translation: "Collapse all" },
      ],
      viewCastingDetailsSectionLabel: "Casting Details",
      viewSentByFormat: "Sent by {sender}.",
      viewRoleAnyAges: "Any playing age",
      viewProductionDetailsSectionLabel: "Production details",
      viewRolesLabel: "{roles} Role(s)",
      viewRoleDisabilityInfoLabel: "Further info:",
      viewProductionDatesLabel: "Production dates:",
      viewRoleAttachmentsLabel: "Attachments",
      viewRoleDisabilityOptions: [
        {
          TypeId: "any",
          Translation: "I am looking for performers <b>with any impairment</b>",
        },
        {
          TypeId: "specific",
          Translation:
            "I am looking for performers <b>with a specific impairment</b>",
        },
        {
          TypeId: "none",
          Translation:
            "I do not want to receive applications from D/deaf or disabled performers and have dispensation in relation to Schedule 9 of the Equality Act 2010",
        },
        {
          TypeId: "undefined",
          Translation:
            "I welcome submissions from D/deaf and/or disabled performers with any impairment",
        },
      ],
      viewTermsText:
        '<p>Please use breakdown content to make suggestions for an opportunity, and have the related client/agent conversations. Sharing confidential content for other purposes will leave liable to the production company, and is a breach of <a href="https://www.spotlight.com/the-small-print/terms-conditions/">Spotlight Terms</a>. You can report any concerns about breakdown content using the Report Concern button.</p>',
      viewProjectSummaryLabel: "Summary",
      viewTermsLabel: "For your eyes only",
      viewRolePlayingAgeLabel: "Playing age:",
      viewRoleGenderLabel: "Gender:",
      viewAttachmentsLabel: "Attachments",
      viewRoleReportConcernButton: "Report concern",
      viewProductionProducerLabel: "Producer:",
      viewRoleDisabilityLabel: "Disability:",
      viewCastingDetailsLabel: "Casting details:",
      hiringPreferenceLabel: "Hiring preference:",
      localHireIsRequired: "Local hire required",
      localHireDescription:
        "This breakdown wishes to receive performer suggestions based within the production location only",
      localHireNoPreferences: "No preference specified",
      castingTypeLabel: "Casting type:",
      castingTypeOnline: "Online",
      castingTypeSelfTape: "Self-Tape",
      castingTypeInPerson: "In-Person",
    },
    viewSuggestions: {
      viewSuggestionsActionCancel: "Cancel",
      viewSuggestionsActionEditSuggestion: "Edit suggestions",
      viewSuggestionsActionRemove: "Remove",
      viewSuggestionsActionSubmit: "Submit profiles",
      viewSuggestionsActionSuggest: "Suggest clients",
      viewSuggestionsCvOptions: [
        {
          TypeId: "current-suggestions",
          Translation: "Show: current suggestions",
        },
        { TypeId: "all-profiles", Translation: "Show: all profiles" },
        { TypeId: "nudges", Translation: "Show: nudges" },
        {
          TypeId: "self-suggestions",
          Translation: "Show: submitted by performer",
        },
      ],
      viewSuggestionsLabelAddComment: "Add comment",
      viewSuggestionsLabelAgenciesFilter: "Which agencies",
      viewSuggestionsLabelAmendment: "Amendment #",
      viewSuggestionsLabelContractExpiryWarning: "Membership ends in XX days",
      viewSuggestionsLabelSelectedCount: "X profiles selected:",
      viewSuggestionsLabelInResponse: "In response to",
      viewSuggestionsLabelLess: "less",
      viewSuggestionsLabelMore: "more",
      viewSuggestionsLabelMorePhotos: "more photos",
      viewSuggestionsLabelNoClientsMessage:
        "Please clear filters to display more results",
      viewSuggestionsLabelNoClientsTitle: "No clients found",
      viewSuggestionsLabelNoSuggestions:
        "You haven't suggested any clients for this role.",
      viewSuggestionsLabelPerformerAge: "XX years, XXX days",
      viewSuggestionsLabelRoleClosed: "This role is closed",
      viewSuggestionsLabelSearchPerformer: "Search performer name…",
      viewSuggestionsLabelSuggestClients: "Suggest clients",
      viewSuggestionsLabelSuggestionsSent: "Suggestions sent",
      viewSuggestionsTitleAgency: "Agency suggestions",
      viewSuggestionsTitleClient: "Client suggestions",
      viewSuggestionsMembershipTypes: [
        { TypeId: "Performer", Translation: "Performer" },
        { TypeId: "Graduate", Translation: "Graduate" },
        { TypeId: "YoungPerformer", Translation: "Young performer" },
        { TypeId: "Presenter", Translation: "Presenter" },
        { TypeId: "Dancer", Translation: "Dancer" },
      ],
      viewSuggestionsVideoShowreelTooltip:
        "This client has a Spotlight video showreel",
      viewSuggestionsVoiceClipsTooltip:
        "This client has voice clips on Spotlight",
      viewSuggestionsMembershipExpires: "Membership ends in {0} day(s)",
      viewSuggestionsLabelClientNudged: "Client nudged",
      viewSuggestionsLabelClientSubmittedThemselves:
        "Client submitted  themselves",
      viewSuggestionsLabelAppearanceFilterLabel: "Appearance",
      viewSuggestionsLabelAppearancesNoResults: "No results, please try again",
      viewSuggestionsLabelAppearancesSearchPlaceholder: "Search",
      locationLabel: "Location",
      searchLocationPlaceholder: "Search",
      viewSuggestionsIsRoleCastButtonText: "This role has been cast",
    },
  },
  [PageKey.Role]: {
    header: {
      breadcrumb: [
        { TypeId: "MyAccount", Translation: "My Account" },
        { TypeId: "JobsFeed", Translation: "Jobs feed" },
      ],
      pageHeaderElements: [
        {
          index: 0,
          label: "Back to jobs feed",
          id: "BackToJobsFeed",
          icon: "https://spotlight-cmsmedia-devaws.s3-eu-west-1.amazonaws.com/media/1193/misc_twoclients.svg",
        },
      ],
    },
    view: {
      viewMessage:
        "<p>Please note that Spotlight is not involved in compiling the job posts which appear on the Spotlight Link. Spotlight is committed to equal opportunities for all performers, irrespective of sex, age, race, disability, religion, sexual orientation, marital status or any other criteria not related to skills or abilities. Where sex, age, height and or/appearance has been specified on a post, the casting director has confirmed that such characteristics are a genuine occupational requirement of the role</p>",
      viewRolesLabel: "Role",
      viewProductionContractTypeLabel: "Contract type:",
      viewSentByAnonymous:
        "The sender has chosen not to show their name or contact details.",
      viewProductionCompanyLabel: "Production company",
      viewRoleDisabilityLabel: "Disability:",
      viewRoleAnyHeight: "Any height",
      viewTermsLabel: "For your eyes only",
      viewCastingDirectorLabel: "Casting director:",
      viewRolePlayingAgeLabel: "Playing age:",
      viewRoleAnyAges: "Any playing age",
      viewProductionDirectorLabel: "Director:",
      viewProjectSummaryLabel: "Summary",
      viewRoleAnyAppearance: "Any appearance",
      viewRoleAppearanceLabel: "Appearance:",
      viewRoleReportConcernButton: "Report Concern",
      viewSentOnFormat: "Sent on {date}",
      viewRoleGenderLabel: "Gender:",
      viewClosingDateLabels: [
        { TypeId: "today", Translation: "Closing today at {time}" },
        { TypeId: "tomorrow", Translation: "Closing tomorrow at {time}" },
        { TypeId: "other", Translation: "Closing: {date} at {time}" },
        { TypeId: "closed", Translation: "Closed" },
      ],
      viewRoleContractTypeLabel: "Contract type:",
      viewProductionDetailsSectionLabel: "Production Details",
      viewRoleDisabilityOptions: [
        {
          TypeId: "any",
          Translation: "I am looking for performers <b>with any impairment</b>",
        },
        {
          TypeId: "specific",
          Translation:
            "I am looking for performers <b>with a specific impairment</b>",
        },
        {
          TypeId: "none",
          Translation:
            "I do not want to receive applications from D/deaf or disabled performers and have dispensation in relation to Schedule 9 of the Equality Act 2010",
        },
        {
          TypeId: "undefined",
          Translation:
            "I welcome submissions from D/deaf and/or disabled performers with any impairment",
        },
      ],
      viewRoleAttachmentsLabel: "Attachments",
      viewProductionDatesLabel: "Production dates:",
      viewAttachmentsLabel: "Attachments",
      viewRoleContractTypeInfoLabel: "Contract info:",
      viewCastingDetailsSectionLabel: "Casting Details",
      viewRoleAnyGender: "Any gender",
      viewTermsText:
        '<p>Please use breakdown content to make suggestions for an opportunity, and have the related client/agent conversations. Sharing confidential content for other purposes will leave liable to the production company, and is a breach of <a href="https://www.spotlight.com/the-small-print/terms-conditions/">Spotlight Terms</a>. You can report any concerns about breakdown content using the Report Concern button.</p>',
      viewProductionLocationLabel: "Production location:",
      viewProjectOverviewLabel: "Project overview",
      viewRoleDisabilityInfoLabel: "Further info:",
      viewProductionProducerLabel: "Producer:",
      viewCastingLocationLabel: "Casting location:",
      viewRolePlayingAgeFormat: "{min}–{max} year(s)",
      viewRoleHeightLabel: "Height:",
      viewCastingDetailsLabel: "Casting details:",
      viewSentByFormat: "Sent by {sender}.",
      hiringPreferenceLabel: "Hiring preference:",
      localHireIsRequired: "Local hire required",
      localHireDescription:
        "This breakdown wishes to receive performer suggestions based within the production location only",
      localHireNoPreferences: "No preference specified",
      castingTypeLabel: "Casting type:",
      castingTypeOnline: "Online",
      castingTypeSelfTape: "Self-Tape",
      castingTypeInPerson: "In-Person",
    },
    makeSuggestion: {
      makeSuggestionActionCancel: "Cancel",
      makeSuggestionActionEdit: "Edit suggestion",
      makeSuggestionActionNudge: "Nudge agent",
      makeSuggestionActionRemove: "Remove suggestion",
      makeSuggestionActionSubmit: "Submit CV",
      makeSuggestionLabelActionDisabled: "This role is closed",
      makeSuggestionLabelMessagePlaceholder: "Add a message…",
      makeSuggestionLabelNudge: "Choose your agent",
      makeSuggestionLabelNudgeDetails: "You nudged {agency} agency on {date}",
      makeSuggestionLabelSelectPhoto: "Select a headshot:",
      makeSuggestionLabelSent: "Sent",
      makeSuggestionLabelSuggestionDetails:
        "You suggested yourself for this role on {date} at {time}",
      makeSuggestionLabelSuggestionMessage: "Message to casting director:",
      makeSuggestionLabelSuggestionNote:
        "Your email address will be sent to the casting director as part of your suggestion",
      makeSuggestionLabelTitle: "Apply for this role",
      makeSuggestionMessageReadAccess:
        "If you are interested in this job, please contact your agent who can discuss the role with you and submit your CV if necessary.",
      makeSuggestionTitleSuggestionMessage:
        "Message to the casting director (optional):",
      makeSuggestionMessageTooltipText:
        "If you would like to send a short message to accompany your CV and photo," +
        " add it here. This step is not mandatory",
      makeSuggestionMessageTooltipTitle: "Message to the casting director",
      makeSuggestionSelectPhotoTooltipText:
        "The larger photo on the left is your main photo, which will be sent to" +
        " the casting director with your job application. If you have a portfolio of photos and would like to choose an alternative image, click a thumbnail below.",
      makeSuggestionSelectPhotoTooltipTitle: "Choose a different photo",
      makeSuggestionIsRoleCastButtonText: "This role has been cast",
      makeSuggestionIsRoleCastNudgeButtonText:
        "This role has been cast (nudge)",
      makeSuggestionLabelNudgeAgent: "Click to nudge {agency} about this job",
      filter: {
        playingAgeFilterLabel: "Playing Age",
        playingAgeFromLabel: "From",
        playingAgeToLabel: "To",
        playingAgeDropdownPlaceholder: "Select",
        playingAgeEmptyLabel: "Select",
        playingAgePostfix: "yr",
        playingAgeOverPostfix: "yrs and over",
        playingAgeLowerPostfix: "yrs and lower",
      },
    },
  },
  [PageKey.FollowUp]: {
    header: {
      breadcrumb: [
        { TypeId: "MyAccount", Translation: "My account" },
        { TypeId: "JobsFeed", Translation: "Jobs feed" },
      ],
      pageHeaderElements: [
        {
          index: 0,
          label: "Back to jobs feed",
          id: "BackToJobsFeed",
          icon: "https://spotlight-cmsmedia-devaws.s3-eu-west-1.amazonaws.com/media/1193/misc_twoclients.svg",
        },
      ],
    },
    body: {
      followupInfoFormat: "This message is a follow-up to {followup}",
      senderInfoFormat: "Sent by {sender}. Sent on {date}",
      dateTimeFormat: "{date} at {time}",
    },
  },
  [PageKey.SelfTapeUpload]: {
    header: {
      breadcrumb: [
        { TypeId: "MyAccount", Translation: "My Account" },
        { TypeId: "SelfTapeRequests", Translation: "Self-tape requests" },
      ],
      stepTracker: [
        {
          Label: "Choose videos",
          Id: "ChooseVideos",
          Index: 0,
        },
        {
          Label: "Add details",
          Id: "AddDetails",
          Index: 1,
        },
      ],
    },
    body: {
      uploadTitle: "Choose videos",
      videosRequiredCount: "Videos required",
      instructions: "Instructions for tape",
      instructionsForAny: "Instructions",
      dropzoneDescription:
        "Click below to upload the videos you want to send. You will be able to send the number requested",
      dropzoneDescriptionAny:
        "Click below to upload the videos you want to send",
      formatValidationError: "Unsupported format",
      uploadError: "Upload failed",
      dropzoneLabel: "Click to select file or drag here",
      closeButtonText: "Cancel and close",
      nextButtonText: "Submit",
      disclaimerText:
        "By sharing a self-tape/video and any associated information, you are confirming you understand that this data is your responsibility as the Data Controller and you are therefore responsible for determining the purposes for that data and ensuring that data is processed and maintained in accordance with relevant data protection laws and other relevant legislation (including those within the UK, EU and other legislation relevant to your territory). including if you choose to share this with other third parties. This also includes the controls you put in place to protect the data and the associated link including expiry dates and disabling the link.",
      anyDescription:
        "Any (You will be able to upload up to 10 separate clips)",
    },
    closeAlert: {
      closeAlertText: "Your changes will not be saved",
      closeAlertTitle: "Are you sure?",
      closePopupButtonText: "Cancel",
      proceedButtonText: "Leave",
    },
    mediaNotUploadedAlert: {
      mediaNotUploadedAlertTitle: "No video uploaded",
      mediaNotUploadedAlertText:
        "Please upload the required quantity of files that is specifed in description",
      mediaNotUploadedAlertCloseButtonText: "Ok",
    },
    mediaExceedLimitAlert: {
      mediaExceedLimitAlertTitle: "Exceed file limit",
      mediaExceedLimitAlertText:
        "You have exceed file limit that was specified by the casting director. To upload new video please delete already existing video.",
      mediaExceedLimitAlertCloseButtonText: "Ok",
    },
  },
  [PageKey.SelfTapeRequest]: {
    header: {
      breadcrumb: [
        { TypeId: "MyAccount", Translation: "My Account" },
        { TypeId: "SelfTapeRequests", Translation: "Self-tape requests" },
      ],
      pageHeaderElements: [
        {
          index: 1,
          label: "Return to self tape requests",
          id: "ReturnToSelfTapeRequests",
          icon: "https://spotlight-cmsmedia.s3-eu-west-1.amazonaws.com/media/2851/left_arrow.svg",
        },
      ],
    },
    body: {
      submitButtonText: "Submit self-tapes",
      closeButtonText: "Cancel and close",
      sentByLabel: "Sent by: {sender}",
      sentOnLabel: "on: {date}",
      expiresLabel: "Expires: {date}",
      breakdownLabel: "Breakdown:",
      attachmentsTitle: "Attachments",
      selfTapesSentTitle: "Self-tapes sent -",
      descriptionTitle: "Description",
      instructionsForTapeLabel: "Instructions for tape",
      videosRequiredLabel: "Videos required:",
      videosRequiredTitle: "Videos required",
      anyDescription:
        "Any (You will be able to upload up to 10 separate clips)",
      instructions: "Instructions",
    },
    alertMessages: {
      selfTapesSent: "Self-tapes sent",
    },
  },
  [PageKey.SelfTapeRequestNotifications]: {
    filter: {
      searchPlaceholder: "Search",
      sortingOptions: [
        {
          TypeId: "dateReceivedRecent",
          Translation: "Sort: Date received (Most recent)",
        },
        {
          TypeId: "dateReceivedOldest",
          Translation: "Sort: Date received (Oldest)",
        },
        {
          TypeId: "dateClosesAscending",
          Translation: "Sort: Date sent (Most recent)",
        },
        {
          TypeId: "dateClosesDescending",
          Translation: "Sort: Date closes (Descending)",
        },
        {
          TypeId: "senderAscending",
          Translation: "Sort: Sender (A-Z)",
        },
        {
          TypeId: "senderDescending",
          Translation: "Sort: Sender (Z-A)",
        },
      ],
      clientDropdownPlaceholder: "Client",
      clientSearchPlaceholder: "Search",
      roleDropdownPlaceholder: "Role",
      dateReceivedInputPlaceholder: "Date received",
    },
    table: {
      clientLabel: "Client: {performerName}",
      tagLabel: "Breakdown: {tag}",
      sentLabel: "Sent: {date}",
      submittedLabel: "Submitted",
      expiryDateLabels: [
        { TypeId: "expired", Translation: "Expired" },
        { TypeId: "today", Translation: "Expires: Today at {time}" },
        { TypeId: "tomorrow", Translation: "Expires: Tomorrow at {time}" },
        { TypeId: "other", Translation: "Expires: {date}" },
      ],
    },
    deleteSelfTapeRequestAlert: {
      closeAlertTitle: "Are you sure?",
      closeAlertText:
        "The file will be deleted and we will not be able to retrieve it. Do you want to download it first?",
      proceedButtonText: "Delete",
      closePopupButtonText: "Cancel",
    },
  },
  [PageKey.AgentQuickSearch]: {
    header: {
      title: "Performer search",
      breadcrumb: [{ TypeId: "MyAccount", Translation: "My Account" }],
    },
    body: {
      agentSearchPlaceholder: "Search performer name...",
      resultStats: "Showing {resultTotal} result(s)",
      noResults: "No results found",
      noResultsFoundDescription: `Find performers Search for a performer by name to see performers' contacts that match your requirements.`,
    },
    footer: {
      footNote: `<p>Performer searches are limited to 10 results. Please try a more detailed search, or sign in to your casting account if you have one.</p> <p>*Spotlight connects its members with professional opportunities. We don’t forward fan mail, so please don’t send it to us.</p>`,
    },
  },
};

export interface IFallbackNavigation {
  [MemberGroups.agent]: INavigation;
  [MemberGroups.castingDirector]: INavigation;
  [MemberGroups.performer]: INavigation;
  [MemberGroups.graduatePerformer]: INavigation;
}

const performerFallbackNav = {
  header: {
    signOut: "Sign out",
    signIn: "Sign in",
    menu: "Menu",
    myAccount: {
      text: "My account",
      actionText: "/myhome/",
    },
  },
  leftHandNavigation: {
    menuNav: [
      {
        text: "Contacts listings",
        link: "/contacts/?utm_source=leftNav&utm_medium=oldSite&utm_campaign=contactsLaunch",
        icon: "https://spotlight-cmsmedia.s3-eu-west-1.amazonaws.com/media/2228/contacts.svg",
        index: 1,
        children: [],
      },
      {
        text: "Jobs feed",
        link: "/jobs/",
        icon: "https://spotlight-cmsmedia.s3-eu-west-1.amazonaws.com/media/2230/search.svg",
        index: 2,
        children: [
          {
            text: "Matching my filter",
            link: "/jobs/matching-my-filters",
            index: 1,
          },
          {
            text: "All paid",
            link: "/jobs/all-paid",
            index: 2,
          },
          {
            text: "All opportunities",
            link: "/jobs/all-opportunities",
            index: 3,
          },
          {
            text: "My Suggestions",
            link: "/jobs/my-suggestions",
            index: 4,
          },
          {
            text: "My tools and settings",
            link: "/settings/",
            index: 5,
          },
        ],
      },
      {
        text: "My profile",
        link: "/profile/",
        icon: "https://spotlight-cmsmedia.s3-eu-west-1.amazonaws.com/media/2229/clients.svg",
        index: 3,
        children: [
          {
            text: "Manage my agents",
            link: "/profile/contacts",
            index: 1,
          },
          {
            text: "Link to my profile",
            link: "/profile/linktoprofile",
            index: 6,
          },
        ],
      },
      {
        text: "Manage media",
        link: "/profile/photos",
        icon: "https://spotlight-cmsmedia.s3-eu-west-1.amazonaws.com/media/2228/media.svg",
        index: 4,
        children: [
          {
            text: "Photos",
            link: "/profile/photos",
            index: 1,
          },
          {
            text: "Video",
            link: "/profile/media/video",
            index: 2,
          },
          {
            text: "Audio",
            link: "/profile/media/audio",
            index: 3,
          },
        ],
      },
      {
        text: "Account settings",
        link: "https://portal.spotlight.com/portal/account/details",
        icon: "https://spotlight-cmsmedia.s3-eu-west-1.amazonaws.com/media/2229/settings.svg",
        index: 5,
        children: [
          {
            text: "My contact details",
            link: "https://portal.spotlight.com/portal/account/details",
            index: 1,
          },
          {
            text: "My privacy settings",
            link: "https://www.spotlight.com/the-small-print/privacy-policy/",
            index: 2,
          },
        ],
      },
      {
        text: "Help",
        link: "https://www.spotlight.com/help-and-faqs/",
        icon: "https://spotlight-cmsmedia.s3-eu-west-1.amazonaws.com/media/2227/help.svg",
        index: 6,
        children: [],
      },
    ],
    bottomNav: [
      {
        text: "Get in touch",
        link: "/get-in-touch",
        index: 7,
      },
      {
        text: "News & Advice",
        link: "https://www.spotlight.com/news-and-advice/",
        index: 8,
      },
      // Temp removed as per https://castingnetworks.atlassian.net/browse/SL-5792?focusedCommentId=374386
      // {
      //   text: "Events",
      //   link: "/news-and-advice/events",
      //   index: 9,
      // },
      {
        text: "Contacts listings",
        link: "/contacts",
        index: 10,
      },
      {
        text: "Discounts",
        link: "/discounts/members",
        index: 11,
      },
      {
        text: "Rooms & Studio Hire",
        link: "https://www.spotlight.com/rooms-and-studios/studios/",
        index: 12,
      },
    ],
  },
  localizationPopUp: {
    languages: [
      {
        label: "English",
        value: "en",
      },
      {
        label: "Française",
        value: "fr",
      },
    ],
    locations: [
      {
        label: "United Kingdom",
        value: "gb",
      },
      {
        label: "Ireland",
        value: "ie",
      },
      {
        label: "France",
        value: "fr",
      },
    ],
    locationTitle: "Location",
    languageTitle: "Language",
    title: "Your location and language",
    cancelButton: "Cancel",
    closeButtonAriaLabel: "Close",
    saveButton: "Save",
    localizationButtonAriaLabel: "Switch language",
  },
  footer: {
    copyrightAddress: "Spotlight, 7 Leicester Place, London, WC2H 7RJ",
    footerItems: {
      footerLinks1: [
        {
          href: "/get-in-touch",
          html: "Get in touch",
        },
        {
          href: "https://jobs.spotlight.com/",
          html: "Work with us",
        },
        {
          href: "https://www.spotlight.com/our-partners/",
          html: "Our Partners",
        },
        {
          href: "/the-small-print/new-on-spotlight/",
          html: "What's new on Spotlight",
        },
        {
          href: "/join-us/how-we-support-our-industry/",
          html: "How we support our industry",
        },
        {
          href: "/the-small-print/what-we-offer-our-members/",
          html: "What we offer our members",
        },
        {
          href: "/the-small-print/promoting-inclusivity-in-our-industry/",
          html: "Promoting inclusivity in our industry",
        },
      ],
      footerLinks2: [
        {
          href: "/calendar/",
          html: "Graduate Performance Calendar",
        },
        {
          href: "/showreel-editing/",
          html: "Showreel Editing",
        },
        {
          href: "/contacts-info",
          html: "Get listed on Contacts",
        },
        {
          href: "https://status.spotlight.com/",
          html: "Website status page",
        },
      ],
      footerLinks3: [
        {
          href: "https://www.spotlight.com/the-small-print/terms-conditions/",
          html: "Terms & Conditions",
        },
        {
          href: "https://www.spotlight.com/the-small-print/privacy-policy/",
          html: "Privacy Policy",
        },
        {
          href: "https://www.spotlight.com/help-and-faqs/",
          html: "Help & FAQs",
        },
      ],
    },
    linkListTitles: ["About Spotlight", "Spotlight Tools", "The Small Print"],
    socialMedia: {
      text: "Follow us",
      elements: [],
    },
  },
  errorPopUp: {
    accountAccessError: {
      accountAccessErrorTitle: "Account access problem",
      accountAccessErrorBody:
        '<p><span>You do not have permission to access this page with your current sign in details. If you require any further help, please get in touch at </span><a rel="noopener noreferrer" href="mailto:questions@spotlight.com" target="_blank" class="c-link">questions@spotlight.com</a><span>.</span></p>',
    },
    networkError: {
      networkErrorTitle: "Network error",
      networkErrorBody:
        "<p><span>Please check your internet connection</span></p>",
    },
    accountError: {
      accountErrorTitle: "Account Error",
      accountErrorBody:
        '<p><span>There is a problem with your account. Please contact <a href="mailto:questions@spotlight.com">Spotlight support</a></span><span> </span><span>to resolve this issue.</span></p>',
    },
    systemError: {
      systemErrorTitle: "System Error",
      systemErrorBody:
        '<p><span>Unable to complete this action. We may be experiencing system errors - please visit the</span><span> <a href="https://status.spotlight.com/">service page</a></span><span> for more information. We apologise for any inconvenience.</span></p>',
      supportErrorCodeLabel:
        "If this issue continues, please provide this code to our support team:",
    },
    buttons: {
      close: "Close",
    },
  },
  common: {
    labels: {
      tooltipCloseButton: "Close",
      tooltipHelpButton: "Help",
      topLabel: "Top",
    },
    page404: {
      page404Header: "Oops!",
      page404Message: "It seems the page is lost.",
      page404Button: "Go to homepage",
    },
    alertMessages: {
      yourChangesWillNotBeSaved: "Your changes will not be saved",
    },
  },
};
